import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    Text,
    Loader,
    Divider,
    Flex,
    AcceptIcon,
    CloseIcon,
    ChevronDownIcon,
    AddIcon,
    LinkIcon,
    ContactGroupIcon,
} from "@fluentui/react-northstar";

// Helper: parse JWT to extract oid (aad_id) and tid (tenant_id)
function parseJwt(token) {
    try {
        if (!token) return null;
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("Invalid Token Parse:", e);
        return null;
    }
}

// Helper: map backend-sanitized renewal error code into German display text.
// Backend never sends raw error blobs — only short safe codes from a fixed
// vocabulary. Anything else falls through to the generic message.
const RENEWAL_ERROR_MESSAGES = {
    conflict: 'Abonnement besteht bereits — wird beim nächsten Zyklus automatisch korrigiert.',
    unauthorized: 'Anmeldung abgelaufen — bitte erneut anmelden.',
    forbidden: 'Zugriff verweigert — Administrator-Zustimmung erforderlich.',
    not_found: 'Abonnement nicht gefunden — wurde möglicherweise extern entfernt.',
    throttled: 'Zu viele Anfragen — automatische Wiederholung läuft.',
    bad_request: 'Ungültige Anfrage — bitte Administrator kontaktieren.',
    server_error: 'Temporärer Serverfehler — wird automatisch erneut versucht.',
    network: 'Netzwerkfehler — wird automatisch erneut versucht.',
    unknown: 'Fehler bei der Erneuerung — wird automatisch erneut versucht.',
};
function humanizeRenewalError(code) {
    if (!code || typeof code !== 'string') return '';
    return RENEWAL_ERROR_MESSAGES[code] || RENEWAL_ERROR_MESSAGES.unknown;
}

// Helper: get API base URL (shared pattern with UsersConfig)
function getApiBaseUrl() {
    let baseUrl = process.env.REACT_APP_TSCM_URL || '';
    const isDevelopment = window.location.hostname === "localhost" || window.location.hostname.includes("devtunnels.ms");
    if (isDevelopment) {
        try { const urlObj = new URL(baseUrl); baseUrl = urlObj.pathname; } catch (e) {}
    }
    return baseUrl;
}

// Color tokens
const COLORS = {
    green: '#107c10',
    red: '#a4262c',
    yellow: '#F7A827',
    gray: '#888',
    purple: '#6264A7',
};

function getStatusColor(status) {
    if (status === 'Active') return COLORS.green;
    if (status === 'Expired') return COLORS.yellow;
    return COLORS.red;
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
        return dateStr;
    }
}

const PresenceSubscriptionCard = ({ token, onStatusChange, refreshKey }) => {
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [renewLoading, setRenewLoading] = useState(false);
    const [stopLoading, setStopLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
    const [noUserGuideOpen, setNoUserGuideOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    const decoded = parseJwt(token);
    const tenantId = decoded ? decoded.tid : null;
    const aadId = decoded ? decoded.oid : null;
    const baseUrl = getApiBaseUrl();

    const fetchStatus = useCallback(async () => {
        if (!tenantId || !token) {
            return;
        }
        setError(null);
        try {
            const url = `${baseUrl}/MSTeamsUser/presenceSubscriptionStatus/${tenantId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.error) {
                setError(data.error);
            } else {
                setSubscriptionData(data);
                if (onStatusChange) onStatusChange(data.status);
            }
        } catch (e) {
            setError(e.message || 'Failed to load subscription status');
        } finally {
            setLoading(false);
        }
    }, [tenantId, token, baseUrl, onStatusChange]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Re-fetch when parent signals an external change (e.g. first user activated)
    useEffect(() => {
        if (refreshKey !== undefined) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    const handleRenew = async () => {
        if ((subscriptionData?.user_count ?? 0) === 0) {
            setNoUserGuideOpen(true);
            return;
        }
        setRenewLoading(true);
        setActionError(null);
        try {
            const url = `${baseUrl}/MSTeamsUser/renewPresenceSubscription`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ tenant_id: tenantId, aad_id: aadId })
            });
            const data = await response.json();
            if (data.error || !response.ok) {
                setActionError(data.error || data.message || 'Renewal failed');
                setTimeout(() => setActionError(null), 5000);
            } else {
                await fetchStatus();
            }
        } catch (e) {
            setActionError(e.message || 'Renewal failed');
            setTimeout(() => setActionError(null), 5000);
        } finally {
            setRenewLoading(false);
        }
    };

    const handleStop = async () => {
        setStopConfirmOpen(false);
        setStopLoading(true);
        setActionError(null);
        try {
            const url = `${baseUrl}/MSTeamsUser/stopPresenceSubscription`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ tenant_id: tenantId, aad_id: aadId })
            });
            const data = await response.json();
            if (data.error || !response.ok) {
                setActionError(data.error || data.message || 'Stop failed');
                setTimeout(() => setActionError(null), 5000);
            } else {
                await fetchStatus();
            }
        } catch (e) {
            setActionError(e.message || 'Stop failed');
            setTimeout(() => setActionError(null), 5000);
        } finally {
            setStopLoading(false);
        }
    };

    const handleCopy = () => {
        const subId = subscriptionData?.subscription_id;
        if (!subId) return;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(subId).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        } else {
            // Fallback for environments without clipboard API
            const textarea = document.createElement('textarea');
            textarea.value = subId;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const status = subscriptionData?.status || 'Inactive';
    const isActive = status === 'Active' || status === 'Expired';
    const statusColor = loading ? COLORS.gray : getStatusColor(status);
    const history = subscriptionData?.history || [];

    const statusPillBg = loading ? '#f3f2f1'
        : status === 'Active' ? 'rgba(16, 124, 16, 0.08)'
        : status === 'Expired' ? 'rgba(247, 168, 39, 0.1)'
        : 'rgba(164, 38, 44, 0.08)';

    return (
        <>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '6px',
                borderLeft: '3px solid #6264A7',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                padding: collapsed ? '12px 20px' : '16px 20px 20px',
                marginBottom: '8px',
            }}>
                {/* Summary row - always visible */}
                <Flex space="between" vAlign="center">
                    <Flex gap="gap.medium" vAlign="center" style={{ flexWrap: 'wrap', rowGap: '6px' }}>
                        <Flex gap="gap.smaller" vAlign="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <ChevronDownIcon size="small" rotate={collapsed ? -90 : 0} style={{ color: '#605e5c' }} />
                            <Text content="Präsenz-Abonnement" weight="semibold" style={{ color: '#1a1a2e', fontSize: '14px' }} />
                        </Flex>

                        <span role="status" aria-label={`Abonnement-Status: ${status}`} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '2px 10px', borderRadius: '12px',
                            backgroundColor: statusPillBg,
                            fontSize: '12px', fontWeight: 600,
                            color: loading ? '#605e5c' : statusColor,
                        }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusColor }} />
                            {loading ? 'Wird geladen...' : status}
                        </span>

                        {collapsed && !loading && isActive && (
                            <Text size="small" style={{ color: '#605e5c' }}
                                content={`${subscriptionData?.user_count || 0} Benutzer \u00b7 läuft ab ${formatDate(subscriptionData?.expiration)}`}
                            />
                        )}
                    </Flex>

                    <Flex gap="gap.small" vAlign="center">
                        {!loading && !error && isActive && (
                            <Button content="Erneuern" size="small" loading={renewLoading}
                                disabled={renewLoading || stopLoading}
                                style={{ backgroundColor: '#6264A7', color: 'white', minWidth: '70px', height: '28px', fontSize: '12px' }}
                                onClick={handleRenew}
                            />
                        )}
                        {!loading && !error && !isActive && (
                            <Button content="Erstellen" size="small" loading={renewLoading}
                                disabled={renewLoading}
                                style={{ backgroundColor: '#6264A7', color: 'white', minWidth: '70px', height: '28px', fontSize: '12px' }}
                                onClick={handleRenew}
                            />
                        )}
                    </Flex>
                </Flex>

                {/* Expanded detail panel */}
                {!collapsed && (
                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edebe9' }}>
                        {loading ? (
                            <Flex hAlign="center" style={{ padding: '12px' }}>
                                <Loader size="small" label="Wird geladen..." />
                            </Flex>
                        ) : error ? (
                            <Flex column gap="gap.small">
                                <Text error content={error} />
                                <Button content="Erneut versuchen" text size="small" onClick={fetchStatus} />
                            </Flex>
                        ) : !isActive ? (
                            <Text content="Kein aktives Abonnement. Klicken Sie auf Erstellen, um die Präsenz-Synchronisierung zu starten." size="small" style={{ color: '#605e5c' }} />
                        ) : (
                            <Flex column gap="gap.small">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px', fontSize: '13px' }}>
                                    <Flex gap="gap.small">
                                        <Text content="Status" weight="semibold" style={{ color: '#1a1a2e', minWidth: '90px' }} />
                                        <Text content={status} weight="bold" style={{ color: getStatusColor(status) }} />
                                    </Flex>
                                    <Flex gap="gap.small">
                                        <Text content="Aktive Benutzer" weight="semibold" style={{ color: '#1a1a2e', minWidth: '90px' }} />
                                        <Text content={String(subscriptionData.user_count)} />
                                    </Flex>
                                    <Flex gap="gap.small">
                                        <Text content="Erstellt" weight="semibold" style={{ color: '#1a1a2e', minWidth: '90px' }} />
                                        <Text content={formatDate(subscriptionData.created_at)} size="small" />
                                    </Flex>
                                    <Flex gap="gap.small">
                                        <Text content="Zuletzt erneuert" weight="semibold" style={{ color: '#1a1a2e', minWidth: '90px' }} />
                                        <Text content={formatDate(subscriptionData.updated_at)} size="small" />
                                    </Flex>
                                    <Flex gap="gap.small">
                                        <Text content="Ablauf" weight="semibold" style={{ color: '#1a1a2e', minWidth: '90px' }} />
                                        <Text content={formatDate(subscriptionData.expiration)} size="small" />
                                    </Flex>
                                    <Flex gap="gap.small">
                                        <Text content="Intervall" weight="semibold" style={{ color: '#1a1a2e', minWidth: '90px' }} />
                                        <Text content={subscriptionData.renewal_interval} size="small" />
                                    </Flex>
                                </div>

                                <Flex gap="gap.small" vAlign="center" style={{ marginTop: '4px' }}>
                                    <Text content="Abo-ID" weight="semibold" size="small" style={{ color: '#1a1a2e' }} />
                                    <Text content={subscriptionData.subscription_id} size="small" style={{ fontFamily: 'monospace', color: '#605e5c' }} />
                                    <Button content={copied ? "Kopiert!" : "Kopieren"} text size="small" onClick={handleCopy}
                                        style={{ minHeight: 'auto', padding: '2px 6px' }} />
                                </Flex>

                                <Flex gap="gap.small" style={{ marginTop: '8px' }}>
                                    <Button content="Erneuerung erzwingen" size="small" loading={renewLoading}
                                        disabled={renewLoading || stopLoading}
                                        style={{ backgroundColor: COLORS.purple, color: 'white' }}
                                        onClick={handleRenew}
                                    />
                                    <Button content="Beenden" size="small" loading={stopLoading}
                                        disabled={renewLoading || stopLoading}
                                        style={{ backgroundColor: COLORS.red, color: 'white' }}
                                        onClick={() => setStopConfirmOpen(true)}
                                    />
                                </Flex>

                                {actionError && <Text error content={actionError} size="small" />}

                                <div style={{ marginTop: '8px', borderTop: '1px solid #edebe9', paddingTop: '8px' }}>
                                    <Button text size="small"
                                        content={
                                            <Flex vAlign="center" gap="gap.smaller">
                                                <ChevronDownIcon size="smaller" rotate={showHistory ? 0 : -90} />
                                                <Text content="Erneuerungsverlauf" size="small" weight="semibold" />
                                            </Flex>
                                        }
                                        onClick={() => setShowHistory(!showHistory)}
                                        aria-expanded={showHistory}
                                        styles={{ minHeight: '32px', padding: 0, justifyContent: 'flex-start' }}
                                    />
                                    {showHistory && (
                                        <Flex column gap="gap.smaller" style={{ marginLeft: '16px', marginTop: '4px' }}>
                                            {history.length === 0 ? (
                                                <Text content="Noch kein Verlauf" size="small" style={{ color: '#605e5c' }} />
                                            ) : history.map(h => (
                                                <Flex key={h.id} gap="gap.small" vAlign="center">
                                                    {h.success == 1
                                                        ? <AcceptIcon style={{ color: COLORS.green }} size="small" />
                                                        : <CloseIcon style={{ color: COLORS.red }} size="small" />}
                                                    <Text content={h.event_type} size="small" />
                                                    <Text content={formatDate(h.created_at)} size="smallest" style={{ color: '#a19f9d' }} />
                                                    {h.error_message && (
                                                        <Text
                                                            content={humanizeRenewalError(h.error_message)}
                                                            size="small"
                                                            error
                                                        />
                                                    )}
                                                </Flex>
                                            ))}
                                        </Flex>
                                    )}
                                </div>

                                <Text size="smallest" style={{ color: '#a19f9d', marginTop: '4px' }}
                                    content="Automatische Erneuerung alle 45 Min." />
                            </Flex>
                        )}
                    </div>
                )}
            </div>

            {/* No active users guidance dialog */}
            {noUserGuideOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 100000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#fff', padding: '24px', borderRadius: '8px',
                        minWidth: '420px', maxWidth: '500px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                        borderTop: '3px solid #6264A7',
                    }}>
                        <Flex column gap="gap.medium">
                            <Text content="Keine aktiven Mitarbeiter" size="large" weight="bold" />
                            <Divider />
                            <Text content="Bevor Sie das Abonnement erneuern können, müssen Sie zunächst Mitarbeiter einrichten:" />
                            <Flex column gap="gap.small" style={{ marginTop: '4px' }}>
                                <Flex gap="gap.small" vAlign="center">
                                    <AddIcon size="small" style={{ color: COLORS.purple, flexShrink: 0 }} />
                                    <Text size="small" content='Mitarbeiter erstellen (falls noch nicht vorhanden)' />
                                </Flex>
                                <Flex gap="gap.small" vAlign="center">
                                    <LinkIcon size="small" style={{ color: COLORS.purple, flexShrink: 0 }} />
                                    <Text size="small" content='Mitarbeiter über "Auto-Map" oder manuell einem Teams-Konto zuordnen' />
                                </Flex>
                                <Flex gap="gap.small" vAlign="center">
                                    <ContactGroupIcon size="small" style={{ color: COLORS.purple, flexShrink: 0 }} />
                                    <Text size="small" content='Präsenz-Toggle für die gewünschten Mitarbeiter aktivieren' />
                                </Flex>
                                <Flex gap="gap.small" vAlign="center" style={{ opacity: 0.55 }}>
                                    <AcceptIcon size="small" style={{ color: COLORS.green, flexShrink: 0 }} />
                                    <Text size="small" content='"Erneuern" erneut klicken (optional — erfolgt automatisch)' />
                                </Flex>
                            </Flex>
                            <Flex hAlign="end" style={{ marginTop: '8px' }}>
                                <Button content="Verstanden" primary
                                    style={{ backgroundColor: '#6264A7', color: 'white' }}
                                    onClick={() => setNoUserGuideOpen(false)}
                                />
                            </Flex>
                        </Flex>
                    </div>
                </div>
            )}

            {/* Stop confirmation dialog */}
            {stopConfirmOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 100000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#fff', padding: '24px', borderRadius: '8px',
                        minWidth: '400px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                        borderTop: '3px solid #a4262c',
                        display: 'flex', flexDirection: 'column'
                    }}>
                        <Flex column gap="gap.medium">
                            <Flex hAlign="center">
                                <Text content="Abonnement beenden" size="large" weight="bold" />
                            </Flex>
                            <Divider />
                            <Text content={`Präsenz-Abonnement beenden? Dies stoppt die Echtzeit-Status-Synchronisierung für ${subscriptionData?.user_count || 0} Mitarbeiter. Das Abonnement läuft innerhalb von 60 Minuten natürlich ab.`} />
                            <Flex hAlign="end" gap="gap.small" style={{ marginTop: '20px' }}>
                                <Button content="Abbrechen" onClick={() => setStopConfirmOpen(false)} />
                                <Button content="Beenden" primary
                                    style={{ backgroundColor: COLORS.red, color: 'white' }}
                                    onClick={handleStop}
                                />
                            </Flex>
                        </Flex>
                    </div>
                </div>
            )}
        </>
    );
};

export { getApiBaseUrl, parseJwt };
export default PresenceSubscriptionCard;
